import { default as React } from 'react';
import { useRef, useEffect, useState, useCallback, useMemo } from 'react';

import { SnykOverview } from 'backstage-plugin-snyk';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { snykApiRef } from 'backstage-plugin-snyk';
import type { Entity } from '@backstage/catalog-model';
import {
  buildSnykUrl,
  buildSnykUrlWithProjectId,
  isValidString,
} from './utils/snykUrlBuilder';

// Constants
const SNYK_LINK_SELECTOR = 'a[href*="/catalog/"][href*="/snyk"]';
const LINK_UPDATE_DELAY = 500; // ms
const DEFAULT_SNYK_HOST = 'app.snyk.io';
const DEFAULT_GITHUB_ORG = 'mediwareinc';

// Types
interface EntityAnnotations {
  'snyk.io/org-name'?: string;
  'snyk.io/org-id'?: string;
  'snyk.io/targets'?: string;
  'snyk.io/project-ids'?: string;
  'github.com/project-slug'?: string;
}

// Utility functions
const getEntityAnnotations = (entity: Entity): EntityAnnotations =>
  (entity.metadata.annotations as EntityAnnotations) || {};

/**
 * Custom wrapper around SnykOverview that redirects "More details" links
 * to Snyk reporting dashboard instead of Backstage Snyk tab
 */
export const CustomSnykOverview: React.FC = () => {
  const { entity } = useEntity();
  const config = useApi(configApiRef);
  const snykApi = useApi(snykApiRef);
  const containerRef = useRef<HTMLDivElement>(null);
  const [orgNameFromApi, setOrgNameFromApi] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchAttempted, setFetchAttempted] = useState(false);

  const snykAppHost = useMemo(
    () => config.getOptionalString('snyk.appHost') ?? DEFAULT_SNYK_HOST,
    [config],
  );

  const entityAnnotations = useMemo(
    () => getEntityAnnotations(entity),
    [entity],
  );

  // Check if required Snyk annotations are present and not blank early
  const snykOrgId = entityAnnotations['snyk.io/org-id'];
  const snykTargets = entityAnnotations['snyk.io/targets'];
  const snykProjectIds = entityAnnotations['snyk.io/project-ids'];
  const shouldShowSnykOverview =
    isValidString(snykOrgId) &&
    (isValidString(snykTargets) || isValidString(snykProjectIds));

  // Fetch organization name from Snyk API using org ID
  const fetchOrgNameFromApi = useCallback(
    async (orgId: string): Promise<string | null> => {
      if (!isValidString(orgId)) {
        return null;
      }

      setIsLoading(true);
      setFetchAttempted(true);

      try {
        const orgSlug = await snykApi.getOrgSlug(orgId);
        const result = isValidString(orgSlug) ? orgSlug : null;

        return result;
      } catch (err) {
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [snykApi],
  );

  // Load organization name from Snyk API when org-id is available
  useEffect(() => {
    if (
      shouldShowSnykOverview &&
      isValidString(snykOrgId) &&
      !fetchAttempted &&
      !isLoading
    ) {
      fetchOrgNameFromApi(snykOrgId).then(setOrgNameFromApi);
    }
  }, [
    shouldShowSnykOverview,
    snykOrgId,
    fetchOrgNameFromApi,
    fetchAttempted,
    isLoading,
  ]);

  // Reset fetch state when org ID changes
  useEffect(() => {
    if (shouldShowSnykOverview) {
      setFetchAttempted(false);
      setOrgNameFromApi(null);
    }
  }, [shouldShowSnykOverview, snykOrgId]);

  // Generate Snyk reporting URL with comprehensive filters
  const getSnykProjectUrl = useCallback((): string | undefined => {
    const snykOrgName = entityAnnotations['snyk.io/org-name'] || orgNameFromApi;
    const targets = entityAnnotations['snyk.io/targets'] || undefined;
    const projectIds = entityAnnotations['snyk.io/project-ids'] || undefined;
    if (targets) {
      const githubSlug = entityAnnotations['github.com/project-slug'];
      const repoTarget =
        githubSlug || `${DEFAULT_GITHUB_ORG}/${entity.metadata.name}`;
      return buildSnykUrl({
        snykAppHost,
        orgName: snykOrgName || '',
        snykTargets: repoTarget,
      });
    }
    return buildSnykUrlWithProjectId({
      snykAppHost,
      orgName: snykOrgName || '',
      projectIds: projectIds,
    });
  }, [entityAnnotations, entity.metadata.name, snykAppHost, orgNameFromApi]);

  const snykProjectUrl = useMemo(
    () => getSnykProjectUrl(),
    [getSnykProjectUrl],
  );

  // Update href attributes of Snyk links to show correct URL on hover
  const updateSnykLinkHrefs = useCallback(() => {
    if (!containerRef.current || !isValidString(snykProjectUrl)) {
      return;
    }

    const snykLinks = containerRef.current.querySelectorAll(
      SNYK_LINK_SELECTOR,
    ) as NodeListOf<HTMLAnchorElement>;

    snykLinks.forEach(link => {
      try {
        // Update the href to show our custom URL on hover
        link.href = snykProjectUrl;
        // Add target and rel attributes for security
        link.target = '_blank';
        link.rel = 'noopener,noreferrer';
      } catch (err) {
        // Silently handle link update errors
      }
    });
  }, [snykProjectUrl]);

  // Handle click on Snyk links as fallback
  const handleSnykLinkClick = useCallback(
    (event: Event) => {
      try {
        const target = event.target as HTMLElement;

        // Detect Snyk links and redirect to custom URL
        const snykLink = target.closest(
          'a[href*="/catalog/"][href*="/snyk"], a[href*="app.snyk.io"]',
        ) as HTMLAnchorElement;

        if (snykLink && isValidString(snykProjectUrl)) {
          event.preventDefault();
          event.stopPropagation();
          window.open(snykProjectUrl, '_blank', 'noopener,noreferrer');
        }
      } catch (err) {
        // Silently handle click errors
      }
    },
    [snykProjectUrl],
  );

  // Setup DOM event listeners and observers for dynamic link updates
  useEffect(() => {
    if (!shouldShowSnykOverview) return undefined;

    const container = containerRef.current;
    if (!container) return undefined;

    let observer: MutationObserver | null = null;
    let timeoutId: NodeJS.Timeout | null = null;

    try {
      // Add click listener for fallback navigation
      container.addEventListener('click', handleSnykLinkClick);

      // Use MutationObserver to watch for dynamic content changes
      observer = new MutationObserver(() => {
        // Clear existing timeout
        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        // Delay to allow DOM updates to complete
        timeoutId = setTimeout(updateSnykLinkHrefs, LINK_UPDATE_DELAY);
      });

      // Start observing changes
      observer.observe(container, { childList: true, subtree: true });

      // Initial update with delay to ensure component is rendered
      timeoutId = setTimeout(updateSnykLinkHrefs, LINK_UPDATE_DELAY);
    } catch (err) {
      // Silently handle setup errors
    }

    // Cleanup function
    return () => {
      try {
        if (container) {
          container.removeEventListener('click', handleSnykLinkClick);
        }
        if (observer) {
          observer.disconnect();
        }
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      } catch (err) {
        // Silently handle cleanup errors
      }
    };
  }, [shouldShowSnykOverview, handleSnykLinkClick, updateSnykLinkHrefs]);

  // Only render if we have the required annotations
  if (!shouldShowSnykOverview) {
    return null;
  }

  return (
    <div ref={containerRef}>
      <SnykOverview />
    </div>
  );
};
